import { useEffect, useRef, useCallback, useState } from 'react';
import createGlobe from 'cobe';

/**
 * CobeGlobe — plain JSX, no arcs mode, visible pinned marker labels.
 * Uses cobe's onRender to track each marker's projected screen position
 * so labels follow the dots as the globe spins.
 */
export function CobeGlobe({
  markers = [],
  arcs = [],
  className = '',
  markerColor = [0.93, 0.17, 0.16],
  baseColor = [0.9, 0.9, 0.9],
  arcColor = [0.93, 0.17, 0.16],
  glowColor = [0.08, 0.08, 0.08],
  dark = 1,
  mapBrightness = 4,
  markerSize = 0.04,
  markerElevation = 0.01,
  arcWidth = 0,
  arcHeight = 0,
  speed = 0.003,
  theta = 0.25,
  diffuse = 1.5,
  mapSamples = 16000,
}) {
  const canvasRef          = useRef(null);
  const wrapRef            = useRef(null);
  const pointerInteracting = useRef(null);
  const lastPointer        = useRef(null);
  const dragOffset         = useRef({ phi: 0, theta: 0 });
  const velocity           = useRef({ phi: 0, theta: 0 });
  const phiOffsetRef       = useRef(0);
  const thetaOffsetRef     = useRef(0);
  const isPausedRef        = useRef(false);
  const phiRef             = useRef(0);           // live phi for label projection
  const thetaTotalRef      = useRef(theta);       // live theta for label projection

  // label positions: { id -> { x, y, visible } }
  const [labelPositions, setLabelPositions] = useState({});

  /* ── pointer handlers ── */
  const handlePointerDown = useCallback((e) => {
    pointerInteracting.current = { x: e.clientX, y: e.clientY };
    if (canvasRef.current) canvasRef.current.style.cursor = 'grabbing';
    isPausedRef.current = true;
  }, []);

  const handlePointerMove = useCallback((e) => {
    if (pointerInteracting.current !== null) {
      const dx = e.clientX - pointerInteracting.current.x;
      const dy = e.clientY - pointerInteracting.current.y;
      dragOffset.current = { phi: dx / 300, theta: dy / 1000 };
      const now = Date.now();
      if (lastPointer.current) {
        const dt = Math.max(now - lastPointer.current.t, 1);
        const maxV = 0.15;
        velocity.current = {
          phi:   Math.max(-maxV, Math.min(maxV, ((e.clientX - lastPointer.current.x) / dt) * 0.3)),
          theta: Math.max(-maxV, Math.min(maxV, ((e.clientY - lastPointer.current.y) / dt) * 0.08)),
        };
      }
      lastPointer.current = { x: e.clientX, y: e.clientY, t: now };
    }
  }, []);

  const handlePointerUp = useCallback(() => {
    if (pointerInteracting.current !== null) {
      phiOffsetRef.current   += dragOffset.current.phi;
      thetaOffsetRef.current += dragOffset.current.theta;
      dragOffset.current = { phi: 0, theta: 0 };
      lastPointer.current = null;
    }
    pointerInteracting.current = null;
    if (canvasRef.current) canvasRef.current.style.cursor = 'grab';
    isPausedRef.current = false;
  }, []);

  useEffect(() => {
    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('pointerup',   handlePointerUp,   { passive: true });
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup',   handlePointerUp);
    };
  }, [handlePointerMove, handlePointerUp]);

  /* ── globe init + label projection (exact cobe math) ── */
  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    let globe       = null;
    let animationId;
    let frameCount  = 0;

    // Exact port of cobe's U() + O() functions
    function toXYZ([lat, lng]) {
      const latR = lat * Math.PI / 180;
      const lngR = lng * Math.PI / 180 - Math.PI; // cobe subtracts PI from lng
      const cosLat = Math.cos(latR);
      return [
        -cosLat * Math.cos(lngR),
         Math.sin(latR),
         cosLat * Math.sin(lngR),
      ];
    }

    function projectPoint(xyz, currentPhi, currentTheta, canvasW, canvasH) {
      const cosTheta = Math.cos(currentTheta);
      const cosPhi   = Math.cos(currentPhi);
      const sinTheta = Math.sin(currentTheta);
      const sinPhi   = Math.sin(currentPhi);
      const [tx, ty, tz] = xyz;
      // rotate by phi then theta — exact cobe order
      const rx =  cosPhi * tx + sinPhi * tz;
      const ry =  sinPhi * sinTheta * tx + cosTheta * ty - cosPhi * sinTheta * tz;
      const rz = -sinPhi * cosTheta * tx + sinTheta * ty + cosPhi * cosTheta * tz;

      const aspect = canvasW / canvasH;
      const sx = (rx / aspect + 1) / 2;
      const sy = (-ry + 1) / 2;
      const visible = rz >= 0 || (rx * rx + ry * ry) < 0.64;

      return { x: sx * canvasW, y: sy * canvasH, visible: rz > 0 };
    }

    function init() {
      const width = canvas.offsetWidth;
      if (width === 0 || globe) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);

      globe = createGlobe(canvas, {
        devicePixelRatio: dpr,
        width,
        height: width,
        phi: 0,
        theta,
        dark,
        diffuse,
        mapSamples,
        mapBrightness,
        baseColor,
        markerColor,
        glowColor,
        markerElevation,
        markers: markers.map((m) => ({ location: m.location, size: markerSize, id: m.id })),
        arcs:    arcs.map((a) => ({ from: a.from, to: a.to, id: a.id })),
        arcColor,
        arcWidth,
        arcHeight,
        opacity: 0.9,
      });

      function animate() {
        if (!isPausedRef.current) {
          phiRef.current += speed;
          if (
            Math.abs(velocity.current.phi)   > 0.0001 ||
            Math.abs(velocity.current.theta) > 0.0001
          ) {
            phiOffsetRef.current   += velocity.current.phi;
            thetaOffsetRef.current += velocity.current.theta;
            velocity.current.phi   *= 0.95;
            velocity.current.theta *= 0.95;
          }
          const tMin = -0.4, tMax = 0.4;
          if (thetaOffsetRef.current < tMin) {
            thetaOffsetRef.current += (tMin - thetaOffsetRef.current) * 0.1;
          } else if (thetaOffsetRef.current > tMax) {
            thetaOffsetRef.current += (tMax - thetaOffsetRef.current) * 0.1;
          }
        }

        const currentPhi   = phiRef.current + phiOffsetRef.current + dragOffset.current.phi;
        const currentTheta = theta + thetaOffsetRef.current + dragOffset.current.theta;
        thetaTotalRef.current = currentTheta;

        globe.update({
          phi:   currentPhi,
          theta: currentTheta,
          dark, mapBrightness, markerColor, baseColor, arcColor, markerElevation,
          markers: markers.map((m) => ({ location: m.location, size: markerSize, id: m.id })),
          arcs:    arcs.map((a) => ({ from: a.from, to: a.to, id: a.id })),
        });

        // Update label positions every 2 frames (perf)
        frameCount++;
        if (frameCount % 2 === 0) {
          const w = canvas.offsetWidth;
          const h = canvas.offsetHeight || w;
          const next = {};
          markers.forEach((m) => {
            const xyz = toXYZ(m.location);
            next[m.id] = projectPoint(xyz, currentPhi, currentTheta, w, h);
          });
          setLabelPositions(next);
        }

        animationId = requestAnimationFrame(animate);
      }
      animate();
      setTimeout(() => { if (canvas) canvas.style.opacity = '1'; });
    }

    if (canvas.offsetWidth > 0) {
      init();
    } else {
      const ro = new ResizeObserver((entries) => {
        if (entries[0]?.contentRect.width > 0) { ro.disconnect(); init(); }
      });
      ro.observe(canvas);
      return () => ro.disconnect();
    }

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
      if (globe) globe.destroy();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers, arcs, markerColor, baseColor, arcColor, glowColor, dark,
      mapBrightness, markerSize, markerElevation, arcWidth, arcHeight,
      speed, theta, diffuse, mapSamples]);

  return (
    <div
      ref={wrapRef}
      className={className}
      style={{ position: 'relative', aspectRatio: '1/1', userSelect: 'none' }}
    >
      {/* Globe canvas */}
      <canvas
        ref={canvasRef}
        onPointerDown={handlePointerDown}
        style={{
          width: '100%',
          height: '100%',
          cursor: 'grab',
          opacity: 0,
          transition: 'opacity 1.2s ease',
          borderRadius: '50%',
          touchAction: 'none',
          display: 'block',
        }}
      />

      {/* Pinned labels — rendered over canvas */}
      {markers.map((m) => {
        const pos = labelPositions[m.id];
        if (!pos) return null;
        const canvasW = canvasRef.current?.offsetWidth  || 1;
        const canvasH = canvasRef.current?.offsetHeight || canvasW;
        const pct_x = (pos.x / canvasW) * 100;
        const pct_y = (pos.y / canvasH) * 100;
        return (
          <div
            key={m.id}
            style={{
              position:   'absolute',
              left:       `${pct_x}%`,
              top:        `${pct_y}%`,
              transform:  'translate(-50%, -180%)',
              pointerEvents: 'none',
              opacity:    pos.visible ? 1 : 0,
              transition: 'opacity 0.4s ease',
              zIndex: 10,
            }}
          >
            {/* Label pill */}
            <div style={{
              background:    'rgba(10,10,10,0.82)',
              border:        '1px solid rgba(236,43,40,0.55)',
              borderRadius:  '4px',
              padding:       '3px 8px',
              fontFamily:    "'Bricolage Grotesque', sans-serif",
              fontSize:      '0.58rem',
              fontWeight:    700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color:         '#fff',
              whiteSpace:    'nowrap',
              backdropFilter:'blur(4px)',
            }}>
              {m.label}
            </div>
            {/* Arrow pointing down to the dot */}
            <div style={{
              width:       0,
              height:      0,
              borderLeft:  '4px solid transparent',
              borderRight: '4px solid transparent',
              borderTop:   '5px solid rgba(236,43,40,0.55)',
              margin:      '0 auto',
            }} />
          </div>
        );
      })}
    </div>
  );
}
